import { constants } from '@config';
import * as TextStylingService from 'src/services/text-styling';

import AbstractElement from './abstractElement';
import { TextOverflowChainHandler } from '../factory/text-overflow/chainHandler';
import { AdvancedOrderedListChainHandler } from 'src/factories/advanced-ordered-list/chain-handler';

export default class TextColumn extends AbstractElement {
  constructor (text, canvas, parent) {
    super();
    this.text = text;
    this.canvas = canvas;
    this.parent = parent;

    this.paragraphParams = {};
    this.orderTracking = {};

    this.textStrings = [];
    this.textLineParams = [];
    this.firstLine = null;
    this.paragraphMaxWidth = 0;
    this.lineIndex = 0;
    this.totalHeight = 0;
    this.maxTotalHeight = 0;

    this.textOverflowChainHandler = new TextOverflowChainHandler();
    this.advancedOrderedListChainHandler = new AdvancedOrderedListChainHandler();
    this.trackingParagraphIndex = 0;
  }

  handleTransform (canvas) {
    const text = this.text;
    const transformList = this.canvas.getTransformList(text.dom.parentElement);
    const isTransformed =
      transformList.numberOfItems === 1 &&
      transformList.getItem(0).type === 4;

    if (!isTransformed) return;

    const xForm = transformList.getItem(0);
    transformList.removeItem(0);
    transformList.appendItem(xForm);
  }

  getColumnBBox (parent) {
    const text = this.text;

    this.bbox = parent.getBBox();

    text.rectBBox = this.bbox;
    text.column = 1;
    text.lineIndex = -1;
    text.rectWidth = (this.bbox.width - (text.columns - 1) * text.columns_gap) / text.columns;
    text.rectHeight = this.bbox.height;

    this.maxTotalHeight = text.rectHeight * text.columns;

    this.oldY = this.bbox.y;
    this.newY = 0;
  }

  sanitizeText () {
    const text = this.text;

    text.textContent = text.textContent.replace(/ /g, constants.text.noBreakSpace);
    text.textContent = text.textContent.replace(/>/g, '&gt;');
    text.textContent = text.textContent.replace(/</g, '&lt;');
  }

  splitContentIntoParagraphs (content) {
    return content.split(/\r\n|\r|\n/g);
  }

  shiftLetterSpacingWhenEdit () {
    const text = this.text;
    text.letter_spacing = TextStylingService.shiftLetterSpacing(text.oldContent, text.textContent, text.cursorPosition, text.newLetters, text.letter_spacing);
  }

  fakeCharIndexToReal (index) {
    const { fakeCharsIndices } = this.text;
    if (!fakeCharsIndices || !fakeCharsIndices.length) return index;

    let realIndex = index;
    for (let i = 0; i < fakeCharsIndices.length; i++) {
      if (fakeCharsIndices[i] >= index) break;
      realIndex--;
    }

    return realIndex;
  }

  shiftLetterSpacingWhenTypewriter (difference) {
    const text = this.text;
    const addedLetterSpacing = Array.from({ length: difference.added }).fill(0);
    const startIndex = this.fakeCharIndexToReal(difference.position);
    const endIndex = this.fakeCharIndexToReal(difference.removed);
    text.letter_spacing.splice(startIndex, endIndex, ...addedLetterSpacing);
  }

  handleLetterSpacing (difference) {
    const text = this.text;

    const shouldShiftLetterSpacing = text.newLetters && Array.isArray(text.letter_spacing);
    if (!shouldShiftLetterSpacing) return;

    if (!Number.isNaN(text.cursorPosition)) {
      this.shiftLetterSpacingWhenEdit();
    } else if (!text.cursorPosition) {
      this.shiftLetterSpacingWhenTypewriter(difference);
    }
  }

  addAttribute (text, attribute, changes, defaultValue, blankArray) {
    const previousLine = changes.lines[0] - 1;
    const newValue = attribute === 'orderedStartArray'
      ? defaultValue
      : text[attribute][previousLine] || defaultValue;

    for (let i = changes.lines.length - 1; i >= 0; i--) {
      text[attribute].splice(changes.lines[i], 0, newValue);
      if (attribute === 'paragraph_spacing' && previousLine >= 0) {
        text[attribute][previousLine] = Math.max(text[attribute][previousLine], 0);
      }
    }
  }

  removeAttribute (text, attribute, changes) {
    const indexToDelete = changes.lines[0] - 1 >= 0 && attribute === 'paragraph_spacing'
      ? changes.lines[0] - 1
      : changes.lines[0];
    text[attribute].splice(indexToDelete, changes.lines.length);
  }

  handleCommonTextSpacing (difference) {
    const text = this.text;
    if (text.newLetters === 0) return;

    const attributeMap = constants.text.paragraphAttributesMap;

    const actionMap = {
      added: this.addAttribute,
      removed: this.removeAttribute
    };

    const cursorPosition = typeof text.cursorPosition === 'number' ? text.cursorPosition : difference.position + text.newLetters;
    const changes = INKIVE.Text.Helpers.getChangedLines(text.oldContent, text.textContent, cursorPosition, text.newLetters);

    for (const attribute in attributeMap) {
      if (!Array.isArray(text[attribute])) continue;

      actionMap[changes.mode]?.(text, attribute, changes, attributeMap[attribute], this.blankArray);
    }
  }

  calculateTextAttributeArray () {
    const text = this.text;
    if (!text.shouldRecalculateStyle) return;

    text.alignmentArray = [];
    text.triggeredArray = [];
    text.bulletArray = [];
    text.bulletStyleArray = [];
    text.orderedArray = [];
    text.orderedStyleArray = [];
    text.orderedNumberingStyleArray = [];
    text.orderedRegexArray = [];
    text.orderedStartArray = [];
    text.headingArray = [];

    for (let i = 0; i < text.strings.length; i++) {
      const string = text.strings[i];
      if (string.isEol || i === text.strings.length - 1) {
        text.alignmentArray.push(string.textAnchor);
        text.triggeredArray.push(string.isButtonTriggered);
        text.bulletArray.push(string.bulletLevel);
        text.bulletStyleArray.push(string.bulletStyle);
        text.orderedArray.push(string.orderedLevel);
        text.orderedStyleArray.push(string.orderedStyle);
        text.orderedStartArray.push(string.orderedStart);
        text.headingArray.push(string.heading);
      }

      if (i === 0 || (i > 0 && text.strings[i - 1].isEol)) {
        text.customBulletSymbolArray.push(string.customBulletSymbol);
        text.bulletFillArray.push(string.bulletFill);
        text.bulletFontSizeArray.push(string.bulletFontSize);
        text.bulletWidthArray.push(string.bulletWidth);
        text.bulletFontFamilyArray.push(string.bulletFontFamily);
        text.bulletCustomFontIdArray.push(string.bulletCustomFontId);
        text.bulletFontStyleArray.push(string.bulletFontStyle);
        text.bulletHighlightArray.push(string.bulletHighlight);

        text.orderedFillArray.push(string.orderedFill);
        text.orderedFontSizeArray.push(string.orderedFontSize);
        text.orderedWidthArray.push(string.orderedWidth);
        text.orderedFontFamilyArray.push(string.orderedFontFamily);
        text.orderedFontWeightArray.push(string.orderedFontWeight);
        text.orderedFontStyleArray.push(string.orderedFontStyle);
        text.orderedHighlightArray.push(string.orderedHighlight);
        text.orderedCustomFontIdArray.push(string.orderedCustomFontId);
        text.orderedNumberingStyleArray.push(string.orderedNumberingStyle);
        text.orderedRegexArray.push(string.orderedRegex);
      }
    }
  }

  handleShiftingAttributes (paragraphs) {
    this.blankArray = paragraphs.map(paragraph => paragraph === '' || paragraph === constants.text.noBreakSpace);
    const difference = INKIVE.Tool.calculateStringContentDifference(this.text.oldContent, this.text.textContent);

    this.handleLetterSpacing(difference);
    this.handleCommonTextSpacing(difference);
  }

  emptyStringDOM () {
    this.text.dom.innerHTML = '';
  }

  setAttributeFromArray (attributeName, spacingArray) {
    if (!Array.isArray(spacingArray)) return;
    this.text.dom.setAttribute(attributeName, spacingArray.join(' '));
  }

  setColumnsGapToDOM () {
    const columnsGap = this.text.columns_gap;
    if (columnsGap) this.text.dom.dataset.columnsGap = columnsGap;
  }

  setColumnsBalanceToDOM () {
    this.text.dom.dataset.balanceColumns = isNaN(this.text.columns_balance) ? 1 : this.text.columns_balance;
  }

  setDataAttributeToDOM () {
    this.setAttributeFromArray('data-dy', this.text.line_spacing);
    this.setAttributeFromArray('data-margin', this.text.line_margin);
    this.setAttributeFromArray('data-paragraph-dy', this.text.paragraph_spacing);
    this.setAttributeFromArray('data-first-line-margin', this.text.first_line_margin);
    this.setAttributeFromArray('data-right-margin', this.text.right_margin);

    this.setOrderedStartToDOM();
    this.setColumnsGapToDOM();
    this.setColumnsBalanceToDOM();
  }

  setOrderedStartToDOM () {
    const hasOrder = this.text.orderedArray.some(Number);

    if (hasOrder && this.text.orderedStart) {
      this.text.dom.dataset.orderedStart = this.text.orderedStart;
    }
  }

  #getContentNodes (textNode) {
    const tspans = [...textNode.querySelectorAll('tspan')];
    const result = [];

    if (tspans.length === 0) {
      result.push(textNode);
    } else {
      const firstTspanClassList = tspans[0].classList;
      if (firstTspanClassList.contains('ordered') || firstTspanClassList.contains('bullets')) {
        tspans.shift();
      }

      result.push(...tspans.filter(tspan => tspan.children.length === 0));
    }

    return result;
  }

  recalculateTextContent () {
    const lastIndex = this.text.dom.childNodes.length - 1;

    this.text.textContent = [...this.text.dom.childNodes].map((text, index) => {
      const ending = (index < lastIndex && text.classList.contains('eol')) ? '\n' : '';
      let lineContent = '';

      if (!text.classList.contains('blank')) {
        for (const node of this.#getContentNodes(text)) {
          lineContent += node.textContent.replace(/ /g, String.fromCharCode(160));
        }
      }

      return lineContent + ending;
    }).join('');
  }

  recalculateCommonTextSpacing () {
    const text = this.text;
    const attributeMap = constants.text.paragraphAttributesMap;

    for (const attribute in attributeMap) {
      if (!Array.isArray(text[attribute])) continue;

      const numOfParagraph = text.textContent.split('\n').length;
      text[attribute] = TextStylingService.mapCommonStylingToArray(text[attribute], text.textContent, numOfParagraph);
    }
  }

  calculateHyperLinkBBox () {
    const tspans = [...this.text.dom.querySelectorAll('tspan')];
    const result = [];

    for (const tspan of tspans) {
      const a = tspan.querySelector('a');
      if (!a) continue;

      const bbox = a.getBBox();
      tspan.dataset.boxCoordinates = `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`;
    }

    return result;
  }

  checkTextIsContinuing () {
    if (!this.parent.parent_text) return;

    const parentLinkedText = this.textOverflowChainHandler.getTextDomByLinkId(this.parent.parent_text);

    if (!parentLinkedText) {
      return;
    }

    this.text.isContinuing = !parentLinkedText.querySelector('text:last-child')?.classList?.contains('eol');

    const orderedData = this.textOverflowChainHandler.getOrderedDataFromPreviousPages(this.parent.parent_text);
    this.orderTracking = TextStylingService.calculateOrderTracking(orderedData.level, orderedData.startFrom, orderedData.style);
  }

  calculateAdvancedOrderedListTracking () {
    const prevOrderedData = INKIVE.AdvancedOrderedListFactory.orderedData;
    const currentPageOrderedData = this.advancedOrderedListChainHandler.getElementOrderedDataOnCurrentPage(this.parent.id);

    const orderedData = {
      style: prevOrderedData.style.concat(currentPageOrderedData.style),
      startFrom: prevOrderedData.startFrom.concat(currentPageOrderedData.startFrom),
      level: prevOrderedData.level.concat(currentPageOrderedData.level)
    };

    const advancedOrderedListTracking = TextStylingService.calculateOrderTracking(orderedData.level, orderedData.startFrom, orderedData.style);

    for (const styleId in advancedOrderedListTracking) {
      if (this.orderTracking?.[styleId]) delete this.orderTracking?.[styleId];

      this.orderTracking[styleId] = advancedOrderedListTracking[styleId];
    }
  }

  renderText (canvas, parent) {
    this.handleTransform(canvas);
    this.getColumnBBox(parent);
    this.sanitizeText();
    this.checkTextIsContinuing();
    this.calculateAdvancedOrderedListTracking();
    this.calculateTextAttributeArray();

    const paragraphs = this.splitContentIntoParagraphs(this.text.textContent);
    this.handleShiftingAttributes(paragraphs);
    this.renderColumns(paragraphs);

    this.parent.child_text && this.recalculateTextContent();
    this.parent.child_text && this.recalculateCommonTextSpacing();
    this.setDataAttributeToDOM();
    this.calculateHyperLinkBBox();
  }

  joinContent (textString, isEol) {
    const content = textString.join(constants.text.noBreakSpace);

    if (content === '') return constants.text.noBreakSpace;
    if (!isEol) return content + constants.text.noBreakSpace;
    return content;
  }

  calculateMaxColumnHeight () {
    const { parent, text } = this;

    if (parent.child_text && text.isTextOverflowRendering) {
      return text.rectHeight;
    }

    const balanceMaxHeight = this.totalHeight / text.columns;
    const normalMaxHeight = text.rectHeight;

    if (text.columns_balance) return null;

    this.splitStringsIntoColumns(normalMaxHeight, false);

    if (this.newY > this.bbox.y + normalMaxHeight) return balanceMaxHeight;

    return normalMaxHeight;
  }

  renderString (textLineIndex, column) {
    const { text, canvas, textStrings, textLineParams } = this;

    const params = textLineParams[textLineIndex];
    const textContent = textStrings[textLineIndex];
    const mainMargin = params.isNewLine && params.factor > -1 ? params.firstLineMargin : params.lineMargin;

    text.strings[textLineIndex] = new INKIVE.Text.String({
      x: this.bbox.x + (text.rectWidth + text.columns_gap) * column + params.factor * mainMargin,
      y: this.newY,
      isNewLine: !!params.isNewLine,
      isEol: !!params.isEol,
      isBlank: !!params?.isBlank,
      bulletLevel: params.bulletLevel,
      bulletContent: params.bulletContent,
      orderedLevel: params.orderedLevel,
      order: params.order,
      orderedStyle: params.orderedStyle,
      orderedNumberingStyle: params.orderedNumberingStyle,
      orderedRegex: params.orderedRegex,
      orderedFill: params.orderedFill,
      orderedWidth: params.orderedWidth,
      orderedFontSize: params.orderedFontSize,
      orderedFontFamily: params.orderedFontFamily,
      orderedFontWeight: params.orderedFontWeight,
      orderedFontStyle: params.orderedFontStyle,
      orderedHighlight: params.orderedHighlight,
      orderedCustomFontId: params.orderedCustomFontId,
      orderedStart: params.orderedStart,
      bulletStyle: params.bulletStyle,
      bulletWidth: params.bulletWidth,
      bulletFontSize: params.bulletFontSize,
      bulletFontStyle: params.bulletFontStyle,
      bulletHighlight: params.bulletHighlight,
      bulletFill: params.bulletFill,
      bulletFontFamily: params.bulletFontFamily,
      bulletCustomFontId: params.bulletCustomFontId,
      startIndex: textLineIndex >= 1 ? text.strings[textLineIndex - 1].endIndex : 0,
      textAnchor: params.lineAnchor,
      line_margin: params.lineMargin,
      first_line_margin: params.firstLineMargin,
      right_margin: params.rightMargin,
      line_spacing: params.lineSpacing,
      paragraph_spacing: params.paragraphSpacing,
      isButtonTriggered: params.isButtonTriggered,
      stringBox: params.stringBox,
      columnIndex: column,
      textContent
    });

    text.strings[textLineIndex].render(canvas, text);
  }

  splitStringsIntoColumns (columnMaxHeight, shouldRenderString) {
    const { text, parent, textStrings, textLineParams } = this;

    let column = 0;
    let columnLineIndex = 0;
    let isFirstParagraphInColumn = 0;

    this.newY = 0;
    this.oldY = this.bbox.y;

    const columnMaxRows = Math.ceil(textStrings.length / text.columns);
    const isTextOverflow = parent.child_text && text.isTextOverflowRendering;
    for (let textLineIndex = 0; textLineIndex < textStrings.length; textLineIndex++) {
      const params = textLineParams[textLineIndex];

      this.newY = params.isNewLine
        ? this.oldY + params.lineHeight + isFirstParagraphInColumn * params.paragraphSpacing
        : this.oldY + params.lineHeight + params.lineSpacing;

      this.oldY = this.newY;

      // If there is no columnMaxHeight, it means that we are handling balanced columns
      const isBalanceColumnFull = !columnMaxHeight && columnLineIndex > columnMaxRows - 1 && column + 1 < text.columns;
      const isNormalColumnFull = columnMaxHeight && this.newY > this.bbox.y + columnMaxHeight && column + 1 < text.columns;
      if (isBalanceColumnFull || isNormalColumnFull) {
        textLineIndex -= 1;
        columnLineIndex = 0;
        column += 1;
        isFirstParagraphInColumn = 0;
        this.newY = 0;
        this.oldY = this.bbox.y;
        continue;
      } else if (isTextOverflow && this.newY > this.bbox.y + columnMaxHeight && column + 1 === text.columns) {
        break;
      }

      columnLineIndex += 1;
      if (shouldRenderString) this.renderString(textLineIndex, column);

      params.isEol && (isFirstParagraphInColumn = 1);
    }
  }

  calculateLineHeight () {
    const { text, textStrings, textLineParams } = this;

    this.totalHeight = 0;
    for (let textLineIndex = 0; textLineIndex < textStrings.length; textLineIndex++) {
      const startIndex = this.getStartIndex(textStrings, textLineIndex);
      const lineHeight = text.getLineFontSize(startIndex, startIndex + textStrings[textLineIndex].length) || text.font_size;

      textLineParams[textLineIndex].startIndex = startIndex;
      textLineParams[textLineIndex].lineHeight = lineHeight;
      textLineParams[textLineIndex].isNewLine = textLineIndex > 0
        ? textLineParams[textLineIndex - 1].isEol
        : true;

      this.totalHeight += textLineParams[textLineIndex].isEol
        ? lineHeight + (textLineParams[textLineIndex + 1]?.paragraphSpacing || 0)
        : lineHeight + textLineParams[textLineIndex].lineSpacing;
    }
  }

  renderColumns (paragraphs) {
    this.calculateTextLineParams(paragraphs);
    this.calculateLineHeight();
    this.emptyStringDOM();

    const maxRow = this.calculateMaxColumnHeight();
    const shouldRenderString = true;

    this.splitStringsIntoColumns(maxRow, shouldRenderString);

    this.text.addHighlightRects(this.canvas, this.text.dom);
  }

  calculateLineMargin (paragraphIndex, bulletWidth, orderedWidth) {
    const text = this.text;
    const lineMargin = Array.isArray(text.line_margin) ? text.line_margin[paragraphIndex] : text.line_margin;

    const maxMargin = text.rectWidth - text.font_size * 0.6 - bulletWidth - orderedWidth;

    return lineMargin > maxMargin
      ? maxMargin
      : lineMargin || 0;
  }

  calculateFirstLineMargin (paragraphIndex, bulletWidth, orderedWidth) {
    const text = this.text;
    const firstLineMargin = Array.isArray(text.first_line_margin)
      ? text.first_line_margin[paragraphIndex]
      : text.first_line_margin;

    const maxMargin = text.rectWidth - text.font_size * constants.text.relativeFontWidth - bulletWidth - orderedWidth;

    return firstLineMargin > maxMargin
      ? maxMargin
      : firstLineMargin || 0;
  }

  calculateRightMargin (paragraphIndex, bulletWidth, orderedWidth) {
    const text = this.text;
    const rightMargin = Array.isArray(text.right_margin)
      ? text.right_margin[paragraphIndex]
      : text.right_margin;

    const maxMargin = text.rectWidth - text.font_size * constants.text.relativeFontWidth - bulletWidth - orderedWidth;

    return rightMargin > maxMargin
      ? maxMargin
      : rightMargin || 0;
  }

  calculateLineAnchor (paragraphIndex) {
    const text = this.text;
    const lineAnchor = Array.isArray(text.alignmentArray)
      ? text.alignmentArray[paragraphIndex]
      : '';

    return lineAnchor || '';
  }

  calculateIsButtonTriggered (paragraphIndex) {
    const text = this.text;
    const isButtonTriggered = Array.isArray(text.triggeredArray) && text.triggeredArray.length
      ? text.triggeredArray[paragraphIndex]
      : false;

    return isButtonTriggered;
  }

  calculateFactor (lineAnchor) {
    const text = this.text;
    const mainAnchor = lineAnchor || text.text_anchor;

    if (!mainAnchor) return 1;

    const reverseFactor = text.direction === 'rtl' ? -1 : 1;
    const alignmentFactor = {
      start: reverseFactor,
      end: -reverseFactor,
      middle: 1,
      justify_start: 1,
      justify_end: -1,
      justify_middle: 1,
      justify_all: 1
    };

    return alignmentFactor[mainAnchor] * reverseFactor;
  }

  calculateBullet (paragraphIndex) {
    return this.text.bulletArray[paragraphIndex] || 0;
  }

  calculateOrder (paragraphIndex) {
    return this.text.orderedArray[paragraphIndex] || 0;
  }

  calculateActualBulletWidth (lineBullet, bulletWidth) {
    if (!lineBullet) return 0;

    return bulletWidth || this.text.font_size;
  }

  calculateActualOrderedWidth (lineOrdered, order, orderedWidth, lineOrderedStyle) {
    if (!lineOrdered) return 0;

    return this.text.calculateOrderedEntryWidth(order, this.text.font_size, orderedWidth, lineOrdered, lineOrderedStyle);
  }

  calculateParagraphSpacing (paragraphIndex) {
    const text = this.text;

    if (!text.paragraph_spacing) return 0;
    if (paragraphIndex === 0) return 0;

    return Array.isArray(text.paragraph_spacing)
      ? text.paragraph_spacing[paragraphIndex - 1]
      : text.paragraph_spacing;
  }

  calculateLineSpacing (paragraphIndex) {
    const text = this.text;

    if (!text.line_spacing) return 0;

    const lineSpacing = Array.isArray(text.line_spacing)
      ? text.line_spacing[paragraphIndex]
      : text.line_spacing;

    return lineSpacing || 0;
  }

  calculateParagraphParams (paragraphIndex) {
    const text = this.text;

    const orderContentArray = TextStylingService.calculateAdvanceOrderContent(
      text.orderedArray,
      text.orderedStartArray,
      text.orderedStyleArray,
      this.orderTracking,
      !!text.isContinuing,
      text.orderedNumberingStyle,
      text.orderedRegexArray
    );
    const orderedLevel = this.calculateOrder(paragraphIndex);
    const order = orderContentArray[paragraphIndex];
    const orderedStyle = text.orderedStyleArray[paragraphIndex] || 0;
    const orderedNumberingStyle = text.orderedNumberingStyleArray[paragraphIndex] || 0;
    const orderedRegex = text.orderedRegexArray[paragraphIndex] || '';
    const orderedFill = text.orderedFillArray[paragraphIndex] || false;
    const orderedFontSize = text.orderedFontSizeArray[paragraphIndex] || 0;
    const orderedFontFamily = text.orderedFontFamilyArray[paragraphIndex] || false;
    const orderedFontWeight = text.orderedFontWeightArray[paragraphIndex] || false;
    const orderedFontStyle = text.orderedFontStyleArray[paragraphIndex] || false;
    const orderedHighlight = text.orderedHighlightArray[paragraphIndex] || false;
    const orderedCustomFontId = text.orderedCustomFontIdArray[paragraphIndex] || false;
    const orderedWidth = orderedLevel ? text.orderedWidthArray[paragraphIndex] || 0 : 0;
    const orderedStart = text.orderedStartArray[paragraphIndex] || false;

    const bulletContentArray = TextStylingService.calculateBulletContent(text.bulletArray, text.bulletStyleArray, text.customBulletSymbolArray);
    const bulletLevel = this.calculateBullet(paragraphIndex);
    const bulletStyle = text.bulletStyleArray[paragraphIndex] || 0;
    const bulletContent = bulletContentArray[paragraphIndex];
    const bulletFontSize = text.bulletFontSizeArray[paragraphIndex];
    const bulletFontStyle = text.bulletFontStyleArray[paragraphIndex];
    const bulletHighlight = text.bulletHighlightArray[paragraphIndex];
    const bulletFill = text.bulletFillArray[paragraphIndex];
    const bulletFontFamily = text.bulletFontFamilyArray[paragraphIndex];
    const bulletCustomFontId = text.bulletCustomFontIdArray[paragraphIndex];
    const bulletWidth = bulletLevel ? text.bulletWidthArray[paragraphIndex] || 0 : 0;

    const actualBulletWidth = this.calculateActualBulletWidth(bulletLevel, bulletWidth);
    const actualOrderedWidth = this.calculateActualOrderedWidth(orderedLevel, order, orderedWidth, orderedStyle);

    const lineMargin = this.calculateLineMargin(paragraphIndex, actualBulletWidth, actualOrderedWidth);
    const firstLineMargin = this.calculateFirstLineMargin(paragraphIndex, actualBulletWidth, actualOrderedWidth);
    const rightMargin = this.calculateRightMargin(paragraphIndex, actualBulletWidth, actualOrderedWidth);
    const lineAnchor = this.calculateLineAnchor(paragraphIndex);
    const isButtonTriggered = this.calculateIsButtonTriggered(paragraphIndex);
    const factor = this.calculateFactor(lineAnchor);
    const lineSpacing = this.calculateLineSpacing(paragraphIndex);
    const paragraphSpacing = this.calculateParagraphSpacing(paragraphIndex);

    this.firstLine = true;
    this.paragraphMaxWidth = text.rectWidth - actualBulletWidth - actualOrderedWidth;
    this.paragraphParams = {
      bulletLevel,
      orderedLevel,
      order,
      bulletContent,
      lineMargin,
      orderedStyle,
      orderedNumberingStyle,
      orderedRegex,
      orderedFill,
      orderedFontSize,
      orderedFontFamily,
      orderedFontWeight,
      orderedFontStyle,
      orderedHighlight,
      orderedCustomFontId,
      orderedStart,
      bulletStyle,
      bulletWidth,
      bulletFontSize,
      bulletFontStyle,
      bulletHighlight,
      bulletFill,
      bulletFontFamily,
      bulletCustomFontId,
      orderedWidth,
      actualBulletWidth,
      actualOrderedWidth,
      firstLineMargin,
      rightMargin,
      lineAnchor,
      isButtonTriggered,
      factor,
      lineSpacing,
      paragraphSpacing,
      paragraphIndex,
      isEol: false,
      isFirstLine: true
    };
  }

  initializeFirstLine (paragraphIndex) {
    this.textStrings[this.lineIndex] = '';
    this.textLineParams[this.lineIndex] = {
      ...this.paragraphParams,
      paragraphIndex,
      isEol: false,
      isFirstLine: true
    };
  }

  handleBlankLine () {
    this.textStrings[this.lineIndex] = constants.text.noBreakSpace;
    this.textLineParams[this.lineIndex].isEol = true;
    this.textLineParams[this.lineIndex].isBlank = true;
    this.lineIndex += 1;
  }

  handleEndOfLine () {
    this.textLineParams[this.lineIndex].isEol = true;
    this.lineIndex += 1;
  }

  wrapParagraphContent (paragraph) {
    const words = paragraph.split(constants.text.noBreakSpace);
    const trimmedWords = paragraph.trimEnd().split(constants.text.noBreakSpace);
    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
      const isSpace = !words[wordIndex];
      const isLastWord = wordIndex >= trimmedWords.length - 1;

      if (isSpace) {
        this.addSpaceAsContent(constants.text.noBreakSpace);
      } else {
        this.addContent(words[wordIndex], true);
        this.addSpace(isLastWord);
      }
    }

    this.handleEndOfLine();
  }

  calculateTextLineParams (paragraphs) {
    this.totalHeight = 0;
    this.trackingParagraphIndex = 0;

    for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex++) {
      if (this.shouldAbortRendering) break;

      const trackingNumOfLine = this.textStrings?.length || 0;
      const paragraph = paragraphs[paragraphIndex];
      const isBlankLine = !paragraph || paragraph === constants.text.noBreakSpace;

      this.calculateParagraphParams(paragraphIndex);
      this.initializeFirstLine(paragraphIndex);

      isBlankLine && this.handleBlankLine();
      isBlankLine || this.wrapParagraphContent(paragraph);

      this.#abortRendering(trackingNumOfLine);
    }
  }

  #abortRendering (trackingNumOfLine) {
    const text = this.text;
    const parent = this.parent;

    if (!parent.child_text || !text.isTextOverflowRendering) {
      this.shouldAbortRendering = false;
      return;
    }

    for (let lineIndex = trackingNumOfLine; lineIndex < this.textStrings.length; lineIndex++) {
      const startIndex = this.getStartIndex(this.textStrings, lineIndex);
      const currentLineLength = this.textStrings[this.lineIndex]?.length || 0;
      const lineHeight = this.text.getLineFontSize(startIndex, startIndex + currentLineLength) || this.text.font_size;

      this.totalHeight += this.textLineParams[lineIndex].isEol
        ? lineHeight + (this.textLineParams[lineIndex + 1]?.paragraphSpacing || 0)
        : lineHeight + this.textLineParams[lineIndex].lineSpacing;

      if (this.totalHeight > this.maxTotalHeight) {
        this.shouldAbortRendering = true;
      }
    }
  }

  shiftToNextLine () {
    this.lineIndex += 1;
    this.firstLine = false;
    this.textLineParams[this.lineIndex] = {
      ...this.paragraphParams,
      isEol: false,
      isFirstLine: this.firstLine
    };
  }

  calculateLineMaxWidth (startIndex, content) {
    const wordSpacingExtraWidth = this.calculateTotalWordSpacing(content);
    const letterSpacingExtraWidth = this.calculateTotalLetterSpacing(startIndex, startIndex + content.length, wordSpacingExtraWidth, content, this.paragraphParams.lineAnchor);

    let margin = this.paragraphParams.lineMargin + this.paragraphParams.rightMargin;

    if (this.firstLine && this.paragraphParams.factor > -1) {
      margin = this.paragraphParams.rightMargin + this.paragraphParams.firstLineMargin;
    } else if (this.firstLine && this.paragraphParams.factor < 1) {
      margin = this.paragraphParams.lineMargin + this.paragraphParams.firstLineMargin;
    }

    return this.paragraphMaxWidth - margin - wordSpacingExtraWidth - letterSpacingExtraWidth;
  }

  addSpaceAsContent (content) {
    if (!content) return;

    const tempContent = (this.textStrings[this.lineIndex] || '') + content;
    const startIndex = this.textStrings.join('').length;
    const lineMaxWidth = this.calculateLineMaxWidth(startIndex, tempContent);
    const tempBox = INKIVE.Text.Helpers.getLineBBox(this.text, startIndex, tempContent, true);

    if (tempContent?.length === 1 || tempBox.width <= lineMaxWidth) {
      this.textStrings[this.lineIndex] = tempContent;
      this.textLineParams[this.lineIndex].stringBox = tempBox;
    } else {
      this.shiftToNextLine();
      this.addSpaceAsContent(content);
    }
  }

  addContent (content, shouldCheck) {
    if (!content) return;

    const contentWithAddedWord = (this.textStrings[this.lineIndex] || '') + content;
    const startIndex = this.textStrings.join('').length + content.length - contentWithAddedWord.length;
    const lineMaxWidth = this.calculateLineMaxWidth(startIndex, contentWithAddedWord);

    if (shouldCheck) {
      const currentBox = INKIVE.Text.Helpers.getLineBBox(this.text, startIndex, contentWithAddedWord + constants.text.noBreakSpace);
      if (currentBox.width <= lineMaxWidth) {
        this.textLineParams[this.lineIndex].stringBox = currentBox;
        this.textStrings[this.lineIndex] = contentWithAddedWord;
        return;
      }
    }

    if (this.textStrings[this.lineIndex] && this.textStrings[this.lineIndex][this.textStrings[this.lineIndex].length - 1] === constants.text.noBreakSpace) {
      this.shiftToNextLine();
      this.addContent(content, true);
    } else {
      const contentWidthAddedChar = (this.textStrings[this.lineIndex] || '') + content.slice(0, 1);
      const newBox = INKIVE.Text.Helpers.getLineBBox(this.text, startIndex, contentWidthAddedChar);

      if (contentWidthAddedChar?.length === 1 || newBox.width <= this.calculateLineMaxWidth(startIndex, contentWidthAddedChar)) {
        this.textLineParams[this.lineIndex].stringBox = newBox;
        this.textStrings[this.lineIndex] = contentWidthAddedChar;

        content = content.slice(1);
        this.addContent(content, false);
      } else {
        this.shiftToNextLine();
        this.addContent(content, false);
      }
    }
  }

  addSpace (isLastWord) {
    if (isLastWord) return;

    this.textStrings[this.lineIndex] += constants.text.noBreakSpace;
  }

  calculateTotalLetterSpacing (startIndex, endIndex, totalWordSpacing, stringContent, lineAnchor) {
    const text = this.text;
    if (lineAnchor.includes('justify')) return 0;

    const numOfLetters = totalWordSpacing
      ? stringContent.split(constants.text.noBreakSpace).join('').length
      : Math.abs(endIndex - startIndex - 1) || 0;

    return Array.isArray(text.letter_spacing)
      ? this.getSum(text.letter_spacing.slice(startIndex, endIndex))
      : text.letter_spacing * numOfLetters;
  }

  getSum (array) {
    let sum = 0;
    for (let i = 0; i < array.length; i++) {
      sum += array[i];
    }

    return sum;
  }

  calculateTotalWordSpacing (content) {
    const text = this.text;

    const numOfWords = Math.abs(content.trimEnd().split(constants.text.noBreakSpace).length - 1);
    return Array.isArray(text.word_spacing)
      ? 0
      : text.word_spacing * numOfWords || 0;
  }

  getStartIndex (textStrings, currentLineIndex) {
    const previousLineIndex = currentLineIndex - 1;
    if (previousLineIndex < 0) return 0;

    let startIndex = 0;
    for (let i = 0; i <= previousLineIndex; i++) {
      const lineContent = textStrings[i];
      startIndex += lineContent.length;
    }

    return startIndex;
  }
}
